import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getCompanyPortalEventRequests } from "../../../../../network/apis/companyPortalEventRequests";
import { getRequestsRequest } from "../../../../../store/Requests/actions";
import { normalizeEventRequests } from "../../Applicants/eventRequestMappers";
import {
  buildAssignableApplicantsParams,
  isAdminZoneApplicantsPortal,
} from "../portal";

const PAGE_SIZE = 10;

/** Approved applicants with no zone yet, paged with a "view more" cursor. */
const useAssignableApplicants = ({ eventId, portalVariant, roleId, lang }) => {
  const isAdmin = isAdminZoneApplicantsPortal(portalVariant);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader);
  const adminList = useSelector(
    (state) => state.requests.unassignApplicants.list
  );
  const adminMeta = useSelector(
    (state) => state.requests.unassignApplicants.meta
  );

  // The cursor is keyed to the role it was paged through with. Resetting it in
  // an effect would let one render fetch the new role at the old page, and the
  // response for page > 1 appends — so a filter change would stack rows from
  // the previous role onto the list.
  const [cursor, setCursor] = useState({ roleId, page: 1 });
  const [companyList, setCompanyList] = useState([]);
  const [companyMeta, setCompanyMeta] = useState({});

  const page = cursor.roleId === roleId ? cursor.page : 1;

  const listParams = useMemo(
    () => ({
      ...buildAssignableApplicantsParams({ eventId }),
      page,
      items: PAGE_SIZE,
      role_id: roleId,
    }),
    [eventId, page, roleId]
  );

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(
      getRequestsRequest({ ...listParams, responseType: "unassignApplicants" })
    );
  }, [dispatch, isAdmin, listParams]);

  useEffect(() => {
    if (isAdmin || !eventId) return undefined;

    let mounted = true;

    getCompanyPortalEventRequests(listParams)
      .then((response) => {
        if (!mounted) return;
        const currentPage = Number(response.headers["current-page"] || 1);
        const items = response.data?.data || [];
        setCompanyList((current) =>
          currentPage === 1 ? items : [...current, ...items]
        );
        setCompanyMeta({
          currentPage,
          count: Number(response.headers["total-count"] || 0),
          pages: Number(response.headers["total-pages"] || 1),
        });
      })
      .catch(() => {
        if (!mounted) return;
        setCompanyList([]);
        setCompanyMeta({});
      });

    return () => {
      mounted = false;
    };
  }, [isAdmin, eventId, listParams]);

  const source = isAdmin ? adminList : companyList;
  const meta = isAdmin ? adminMeta : companyMeta;

  const items = useMemo(
    () => normalizeEventRequests(source || [], lang),
    [source, lang]
  );

  const loadMore = useCallback(
    () => setCursor({ roleId, page: page + 1 }),
    [roleId, page]
  );

  return {
    items,
    isLoading,
    hasMore: Boolean(items.length) && page < (meta?.pages || 1),
    loadMore,
  };
};

export default useAssignableApplicants;
